import { Task, Takeaway } from '../types';

/**
 * Compare editable fields of a task to detect actual changes
 * Ignores read-only fields like id, confidence, evidence, etc.
 */
export function hasTaskChanged(current: Task, original: Task): boolean {
  // Compare only editable fields that users can modify
  return (
    current.title !== original.title ||
    current.description !== original.description ||
    current.owner !== original.owner ||
    current.requester !== original.requester ||
    current.due !== original.due ||
    current.priority !== original.priority ||
    JSON.stringify(current.tags?.sort()) !== JSON.stringify(original.tags?.sort()) // Compare sorted arrays
  );
}

/**
 * Compare editable fields of a takeaway to detect actual changes
 * Ignores read-only fields like id, confidence, evidence, etc.
 */
export function hasTakeawayChanged(current: Takeaway, original: Takeaway): boolean {
  // Compare only editable fields that users can modify
  return (
    current.name !== original.name ||
    current.description !== original.description ||
    JSON.stringify(current.tags?.sort()) !== JSON.stringify(original.tags?.sort()) // Compare sorted arrays
  );
}

/**
 * Get only the tasks that have actually changed
 */
export function getModifiedTasks(currentTasks: Task[], originalTasks: Task[]): Task[] {
  const originalMap = new Map(originalTasks.map(t => [t.id, t]));
  
  return currentTasks.filter(task => {
    // Exclude processed tasks from change detection (locked tasks cannot be modified)
    if (task.isProcessed) return false;
    
    const original = originalMap.get(task.id);
    if (!original) return false; // New task (shouldn't happen in edit scenario)
    return hasTaskChanged(task, original);
  });
}

/**
 * Get only the takeaways that have actually changed
 */
export function getModifiedTakeaways(currentTakeaways: Takeaway[], originalTakeaways: Takeaway[]): Takeaway[] {
  const originalMap = new Map(originalTakeaways.map(t => [t.id, t]));
  
  return currentTakeaways.filter(takeaway => {
    const original = originalMap.get(takeaway.id);
    if (!original) return false; // New takeaway (shouldn't happen in edit scenario)
    return hasTakeawayChanged(takeaway, original);
  });
}

